const {
    getShortLanguageCode,
    getBuyerAssumedLanguage,
    getSellerValidatedLanguages,
    DEFAULT_FIVERR_LANGUAGE
} = require('@fiverr-private/localization');

const isBuyerLanguageDefault = (buyerAssumedLanguage) => buyerAssumedLanguage === DEFAULT_FIVERR_LANGUAGE;

const isBuyerSpeakSellerLanguage = (buyerAssumedLanguage, sellerValidatedLanguages) =>
    sellerValidatedLanguages.some((languageObj) =>
        getShortLanguageCode(languageObj.name) === buyerAssumedLanguage
    );

const shouldShowSellerLanguage = (buyerAssumedLanguage, sellerValidatedLanguages) =>
    !isBuyerLanguageDefault(buyerAssumedLanguage) && isBuyerSpeakSellerLanguage(buyerAssumedLanguage, sellerValidatedLanguages);

/**
 * Returns the seller validated languages with the common language
 * with the buyer as the first item in the returned array
 * @param buyerAssumedLanguage {String} -
 *      The assumed language the buyer speaks, for example: 'en', 'de'
 * @param proficientLanguages {Array<{name: String, level: Number}>} -
 *      Array of language objects the seller declares he speaks,
 *      for example: [{name:'en', level:4}]
 * @param countryCode {String} -
 *      The country (code) the seller comes from,
 *      for example: 'DE', 'US
 * @returns {Array<{name: String, level: Number}>} -
 *      Array of the seller language objects validated by a business logic,
 *      sorted by the common language with the buyer, and by the level (desc)
 */
const getSortedSellerValidatedLanguages = (buyerAssumedLanguage, { proficientLanguages, countryCode }) => {
    const sellerValidatedLanguages = getSellerValidatedLanguages({ proficientLanguages, countryCode });
    const commonLanguageIndex = sellerValidatedLanguages.findIndex((languageObj) => getShortLanguageCode(languageObj.name) === buyerAssumedLanguage);
    if (commonLanguageIndex > -1) {
        const [ commonLanguage ] = sellerValidatedLanguages.splice(commonLanguageIndex, 1);
        sellerValidatedLanguages.unshift(commonLanguage);
    }

    return sellerValidatedLanguages;
};

/**
 * Given buyer params (most probably from the context)
 * and seller params (proficient languages and country),
 * decide what is the assumed buyer language,
 * what are the validated seller and check if they speak the same language.
 * @param buyer {{locale: String, browserLanguage: String, countryCode: String}} -
 *      params regarding the buyer that should come from the context
 * @param seller {{proficientLanguages: Array<{name: String, level: Number}>, countryCode: String}} -
 *      the languages the seller declares he speaks and his country
 * @returns {{
 *      shouldShowSellerLanguage: Boolean,
 *      commonLanguage: {name: String, level: Number},
 *      sellerValidatedLanguages: Array<{name: String, level: Number}>,
 *      buyerAssumedLanguage: string, sellerCountryCode: string}}
 */
const getSellerLanguageParams = ({ buyer, seller }) => {
    const { locale, browserLanguage, countryCode: buyerCountryCode } = buyer;
    const { proficientLanguages, countryCode: sellerCountryCode } = seller;
    const buyerAssumedLanguage = getBuyerAssumedLanguage({ locale, browserLanguage, countryCode: buyerCountryCode });
    const sellerValidatedLanguages = getSortedSellerValidatedLanguages(buyerAssumedLanguage, { proficientLanguages, countryCode: sellerCountryCode });
    const showSellerLanguage = shouldShowSellerLanguage(buyerAssumedLanguage, sellerValidatedLanguages);
    const commonLanguage = isBuyerSpeakSellerLanguage(buyerAssumedLanguage, sellerValidatedLanguages) ? sellerValidatedLanguages[0] : null;

    return {
        buyerAssumedLanguage,
        sellerValidatedLanguages,
        sellerCountryCode,
        shouldShowSellerLanguage: showSellerLanguage,
        commonLanguage
    };
};

module.exports = {
    shouldShowSellerLanguage,
    getSellerLanguageParams
};
